export const useAppStateStore = defineStore('appState', {
  state: () => ({
    appState: {},
    initialized: false,
    loading: false,
    error: null
  }),

  actions: {
    async fetchAppState({userId = useSessionStore().currentUserId, throwOnError = false} = {}) {
      const session = useSessionStore();
      const isCurrentUser = () => String(session.currentUserId) === String(userId);
      this.loading = true
      this.initialized = false;
      this.error = null
      try {
        const data = await useLocalApi().fetch(`/app-state/${userId}`);
        if (!isCurrentUser()) throw new Error('The active account changed.');
        if (throwOnError && (!data || typeof data !== 'object' || Array.isArray(data))) {
          throw new Error('Could not load app-state.');
        }
        this.appState = data || {};
        this.initialized = true;
        return this.appState;
      } catch (err) {
        if (isCurrentUser()) this.error = err;
        if (throwOnError) throw err;
      } finally {
        if (isCurrentUser()) this.loading = false;
      }
    },

    // Explicit Account tools edits are allowed while managing another account.
    // Ordinary onboarding writes retain the impersonation guard below.
    async updateOnboardingState({userId, changes = {}, replace = false}) {
      const session = useSessionStore();
      const isCurrentUser = () => userId != null && String(session.currentUserId) === String(userId);
      if (!isCurrentUser()) throw new Error('The active account changed.');
      if (!changes || typeof changes !== 'object' || Array.isArray(changes)
        || Object.values(changes).some(value => value !== null
          && (!['boolean', 'string', 'number'].includes(typeof value)
            || typeof value === 'number' && !Number.isFinite(value)))) {
        throw new Error('Invalid onboarding values.');
      }

      this.loading = true;
      this.error = null;
      try {
        const data = await useLocalApi().fetch(`/app-state/${userId}`, {
          method: 'PUT',
          body: {onboarding: changes},
          ...(replace ? {query: {replaceOnboarding: 'true'}} : {})
        });
        if (!isCurrentUser()) throw new Error('The active account changed.');
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Could not save app-state.');
        this.appState = data;
        return this.appState;
      } catch (err) {
        if (isCurrentUser()) this.error = err;
        throw err;
      } finally {
        if (isCurrentUser()) this.loading = false;
      }
    },

    async updateAppState({userId = useSessionStore().currentUserId, updates = {}} = {}) {
      if (useSessionStore().isImpersonating) {
        return;
      }

      this.loading = true;
      this.error = null;
      try {
        const data = await useLocalApi().fetch(`/app-state/${userId}`, {
          method: 'PUT',
          body: updates
        });
        this.appState = data || {};

      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    }
  },

  getters: {
    getDigitizationKey: state => (projectId, key) => (
      state.appState.digitization && state.appState.digitization[projectId] && state.appState.digitization[projectId][key]
    )
  }
})
